import React from 'react';
import { Banner, BlockStack, Button, Text } from '@shopify/polaris';
import { useTranslation } from '../i18n';

/**
 * SessionBanner — renders a Polaris banner when the Shopify session is
 * missing or errored. Used at the top of pages that require shop creds.
 *
 * Props:
 *   - error:         string | null — populated when the session fetch failed
 *   - authenticated: boolean
 *   - reinstallUrl:  string         — link that kicks off /api/auth/install
 *
 * Returns null when authenticated cleanly.
 */
export default function SessionBanner({ error, authenticated, reinstallUrl }) {
  const { t } = useTranslation();
  if (authenticated) return null;

  if (error) {
    return (
      <Banner tone="critical" title={t('ui.session.title')}>
        <BlockStack gap="200">
          <Text as="p">{t('ui.session.body')}</Text>
          <details className="au-error-raw">
            <summary>{t('ui.errors.showDetails')}</summary>
            <code>{String(error)}</code>
          </details>
          <div>
            <Button variant="primary" url={reinstallUrl} external>
              Reconnect Shopify
            </Button>
          </div>
        </BlockStack>
      </Banner>
    );
  }

  return (
    <Banner tone="warning">
      <BlockStack gap="200">
        <Text as="p">
          Your Shopify session has expired or isn't connected. Reconnect to
          continue using Autonoma.
        </Text>
        <div>
          <Button variant="primary" url={reinstallUrl} external>
            Reconnect Shopify
          </Button>
        </div>
      </BlockStack>
    </Banner>
  );
}
